<template>
  <section>
    <main>
    <h3>AtRiskAccounts</h3>
    <div class="card px-2 py-3 mb-1 shadow-sm d-flex">
      <div class="d-flex flex-wrap align-items-center">
        <div
          class="input-group input-group-sm p-1 treeselect-wrapper"
          style="min-width: 280px;"
          v-if="oliverSalesLoaded"
        >
          <div class="input-group-prepend">
            <span class="input-group-text">Account Manager</span>
          </div>
          <IndustrySearch
            :option_list="userOliverSales"
            :default_values="userOliverSale"
            :key="atRiskKey"
            :multiple="false"
            :isDisabled="loading"
            @selected="selectOliverSale"
            class="form-control inline-table"
          />
        </div>
      </div>
    </div>

    <div v-if="loading" class="d-flex align-items-center justify-content-center p-5">
      <LoadingSpinner />
    </div>

    <div v-else-if="!loading && clients.length === 0" class="alert alert-info">
      No clients with meeting rate drop criteria for the selected filter.
    </div>

    <div v-else id="atRiskAccountsAccordion">
      <p class="mb-3 font-weight-bold">
        Total: {{ clients.length }}
      </p>
      <div
        v-for="client in clients"
        :key="client.id"
        class="border rounded p-3 mb-3 bg-white"
      >
        <div class="d-flex flex-wrap align-items-center">
          <div class="flex-shrink-0">
            <a
              :href="`/clients/${client.id}`"
              target="_blank"
              class="text-primary font-weight-bold"
            >
              {{ client.client_name }}
            </a>
            <span
              v-if="getCpiLabel(client)"
              class="rounded-pill mx-2 px-2 py-1 small text-white font-weight-normal"
              :style="getCpiBadgeStyle(client)"
            >
              {{ getCpiLabel(client) }}
            </span>
            <!-- :class="{ 'comment-icon-warning': client.time_warning == '1' }" -->
            <button
              type="button"
              class="btn btn-link p-0 ml-1 align-middle border-0"
              :class="{ 'comment-icon-warning': client.time_warning == '1' }"
              aria-label="Ver comentarios"
              @click="openComments(client)"
            >
              <img
                src="@/assets/images/comment.png"
                alt="comment"
                width="16"
                height="16"
              />
            </button>
          </div>
          <div class="d-flex flex-wrap align-items-center ml-auto" style="min-width: 0">
            <template v-if="callKpiByClient[client.id]">
              <span
                class="bg-white text-dark border rounded px-2 py-1 small mr-2 mb-2"
              >
                Call 14D : {{ formatNumber(callKpiByClient[client.id].total_cold_call_avg_14_days) }}
              </span>
              <span
                class="bg-white text-dark border rounded px-2 py-1 small mr-2 mb-2"
              >
                Call 30D : {{ formatNumber(callKpiByClient[client.id].total_cold_call_avg_30_days) }}
              </span>
              <span
                class="bg-white text-dark border rounded px-2 py-1 small mr-2 mb-2"
              >
                CR Call 14D : {{ formatPercent(callKpiByClient[client.id].avg_conversion_rate_14_days) }}
              </span>
              <span
                class="bg-white text-dark border rounded px-2 py-1 small mr-2 mb-2"
              >
                CR Call 30D : {{ formatPercent(callKpiByClient[client.id].avg_conversion_rate_30_days) }}
              </span>
            </template>
            <span
              v-if="!callKpiByClient[client.id]"
              class="bg-white text-dark border rounded px-2 py-1 small mr-2 mb-2"
            >
              <button
                type="button"
                class="btn btn-info btn-sm d-inline-flex align-items-center"
                :disabled="isCallKpiLoading(client.id)"
                @click="loadCallKpiStats(client.id)"
              >
                <i
                  v-if="isCallKpiLoading(client.id)"
                  class="fa fa-spinner fa-spin mr-1"
                  aria-hidden="true"
                />
                Load call stats
              </button>
            </span>
            <span
              class="bg-white text-dark border rounded px-2 py-1 small mr-2 mb-2"
            >
              CR 6M : {{ formatPercent(client.meeting_rate) }}
            </span>
            <span
              class="border rounded px-2 py-1 small mr-2 mb-2"
              :class="client.alert_meeting_rate === 'yes' ? 'bg-danger text-white border-danger' : 'bg-white text-dark'"
            >
              CR 30D : {{ formatPercent(client.meeting_rate_this_month) }}
            </span>
            <span
              class="bg-white text-dark border rounded px-2 py-1 small mr-2 mb-2"
            >
              Sent 6M : {{ formatNumber(client.total_contacts_day) }}
            </span>
            <span
              class="border rounded px-2 py-1 small mr-2 mb-2"
              :class="formatContacts(client.alert_contact_day, client.alert_contact_day_up)"              
            >
              Sent 30D : {{ formatNumber(client.total_contacts_day_this_month) }}
            </span>
            <span
              class="bg-white text-dark border rounded px-2 py-1 small mr-2 mb-2"
            >
              Step1 6M : {{ formatNumber(client.total_contacts_day_step1) }}
            </span>
            <span
              class="border rounded px-2 py-1 small mr-2 mb-2"         
              :class="formatContacts(client.alert_contact_day_step1, client.alert_contact_day_step1_up)"                                 
            >
              Step1 7D : {{ formatNumber(client.total_contacts_day_this_month_step1) }}
            </span>
            <button
              type="button"
              class="btn btn-primary btn-sm d-inline-flex align-items-center mr-2 mb-2"
              :disabled="isDiagnosticLoading(client.id)"
              @click="requestDiagnostic(client)"
            >
              <i
                v-if="isDiagnosticLoading(client.id)"
                class="fa fa-spinner fa-spin mr-1"
                aria-hidden="true"
              />
              {{ isDiagnosticLoading(client.id) ? "Chargement..." : "Diagnostic IA" }}
            </button>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-link p-1 ml-2 text-secondary flex-shrink-0 collapsed"
            data-toggle="collapse"
            :data-target="`#orders-${client.id}`"
            aria-expanded="false"
            :aria-controls="`orders-${client.id}`"
            :aria-label="`Ver campañas de ${client.client_name}`"
            @click="loadClientOrders(client.id)"
          >
            <i
              class="fa fa-chevron-down"
              aria-hidden="true"
            />
          </button>
        </div>
        <div
          :id="`orders-${client.id}`"
          class="collapse accordion-content w-100 mt-3 pt-3 border-top"
        >
          <div class="card mb-3">
            <div class="card-body">
              <h6 class="font-weight-bold mb-3">Diagnostic IA</h6>
              <div v-if="isDiagnosticLoading(client.id)" class="text-muted">
                Chargement du diagnostic...
              </div>
              <div v-else-if="diagnosticError[client.id]" class="text-danger">
                Le diagnostic a échoué, réessayez.
              </div>
              <div
                v-else-if="diagnosticResult[client.id]"
                class="diagnostic-result mb-0"
                v-html="convertDiagnosticText(diagnosticResult[client.id])"
              />
              <div v-else class="text-muted">
                Cliquez sur Diagnostic IA pour générer le diagnostic.
              </div>
            </div>
          </div>
          <div class="table-responsive m-0 p-0">
            <Orders
              v-if="loadedClientOrders[client.id]"
              :fromAtRiskAccounts="true"
              :client_id="Number(client.id)"
            />
          </div>
        </div>
      </div>
    </div>
  </main>
  <Comments
    :modal-name="commentsModalName"
    :client="selectedClientForComments"
    :notes="commentsNotes"
    :visible="commentsModalVisible"
    :loading="commentsLoading"
    @publish="publishNote"
    @close="closeComments"
  />
  </section>
</template>

<script>
import { clientsService, usersService } from "@/_services";
import IndustrySearch from "@/components/AccountCreation/Steps/Campaign/search/IndustrySearch.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Orders from "@/components/Onboarding/Orders.vue";
import Comments from "@/components/Common/Comments.vue";
import { mapState } from "vuex";

export default {
  name: "AtRiskAccounts",
  components: {
    IndustrySearch,
    LoadingSpinner,
    Orders,
    Comments,
  },
  data() {
    return {
      commentsModalName: "at-risk-comments-modal",
      loading: false,
      oliverSalesLoaded: false,
      userOliverSales: [],
      userOliverSale: ["0"],
      accountManagerId: null,
      clients: [],
      atRiskKey: 0,
      loadedClientOrders: {},
      callKpiByClient: {},
      callKpiLoading: {},
      diagnosticLoading: {},
      diagnosticResult: {},
      diagnosticError: {},
      selectedClientForComments: null,
      commentsNotes: [],
      commentsModalVisible: false,
      commentsLoading: false,
    };
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.authentication.user,
    }),
    isAccountManager() {
      return this.currentUser && this.currentUser.role_id == 1 && this.currentUser.user_constraint_id == 2;
    },
    formatContacts() {
      return (alert_contact_day, alert_contact_day_up) => {        
        if(alert_contact_day == 'yes') {
          return 'bg-danger text-white border-danger' 
        } else {
          if(alert_contact_day_up == 'yes') {
            return 'bg-success text-white'
          } else {
            return 'bg-white text-dark'
          }
        }
      }
    }
  },
  created() {
    this.loadAccountManagers();
  },
  mounted() {
    if (this.oliverSalesLoaded) {
      this.getData();
    }
  },
  watch: {
    oliverSalesLoaded(val) {
      if (val) this.getData();
    },
  },
  methods: {
    loadAccountManagers() {
      this.loading = true;
      usersService
        .getUserOliverSales()
        .then((data) => {
          const list = (data.result || []).map((user) => ({
            id: String(user.id),
            label: `${user.name || ""} ${user.lastname || ""}`.trim(),
          }));
          this.userOliverSales = [{ id: "0", label: "Select option..." }, ...list];
          if (this.isAccountManager && this.currentUser) {
            this.userOliverSale = [String(this.currentUser.id)];
            this.accountManagerId = this.currentUser.id;
          }
          this.oliverSalesLoaded = true;
        })
        .catch((err) => {
          console.error("Error loading account managers:", err);
          this.oliverSalesLoaded = true;
        });
    },
    selectOliverSale(selected) {
      if (selected && selected.id != null && String(selected.id) !== "0") {
        this.accountManagerId = selected.id;
        this.userOliverSale = [String(selected.id)];
      } else {
        this.accountManagerId = null;
        this.userOliverSale = ["0"];
      }
      this.getData();
    },
    getData() {
      this.loading = true;
      const accountManager = this.accountManagerId != null ? this.accountManagerId : 0;
      clientsService
        .getClientsMeetingRateDrop(accountManager)
        .then((res) => {
          this.clients = (res && res.result) ? res.result : [];
          this.loadedClientOrders = {};
          this.callKpiByClient = {};
          this.callKpiLoading = {};
          this.diagnosticLoading = {};
          this.diagnosticResult = {};
          this.diagnosticError = {};
          this.loading = false;
        })
        .catch((err) => {
          console.error("Error loading at-risk clients:", err);
          this.clients = [];
          this.loadedClientOrders = {};
          this.callKpiByClient = {};
          this.callKpiLoading = {};
          this.diagnosticLoading = {};
          this.diagnosticResult = {};
          this.diagnosticError = {};
          this.loading = false;
        });
    },
    loadClientOrders(clientId) {
      if (!this.loadedClientOrders[clientId]) {
        this.$set(this.loadedClientOrders, clientId, true);
      }
    },
    isCallKpiLoading(clientId) {
      return Boolean(this.callKpiLoading[clientId]);
    },
    loadCallKpiStats(clientId) {
      return this.fetchCallKpiStats(clientId);
    },
    fetchCallKpiStats(clientId) {
      if (this.callKpiByClient[clientId]) {
        return Promise.resolve(this.callKpiByClient[clientId]);
      }
      this.$set(this.callKpiLoading, clientId, true);
      return clientsService
        .getCallKpiRiskAccounts(clientId)
        .then((res) => {
          const raw =
            res &&
            (typeof res.result === "object" && res.result !== null && !Array.isArray(res.result)
              ? res.result
              : res) || {};
          const callKpis = {
            total_cold_call_avg_14_days: raw.total_cold_call_avg_14_days,
            total_cold_call_avg_30_days: raw.total_cold_call_avg_30_days,
            avg_conversion_rate_14_days: raw.avg_conversion_rate_14_days,
            avg_conversion_rate_30_days: raw.avg_conversion_rate_30_days,
          };
          this.$set(this.callKpiByClient, clientId, callKpis);
          return callKpis;
        })
        .catch((err) => {
          console.error("Error loading call KPI for client:", err);
          throw err;
        })
        .finally(() => {
          this.$set(this.callKpiLoading, clientId, false);
        });
    },
    isDiagnosticLoading(clientId) {
      return Boolean(this.diagnosticLoading[clientId]);
    },
    requestDiagnostic(client) {
      this.$set(this.diagnosticLoading, client.id, true);
      this.$set(this.diagnosticError, client.id, false);
      this.$set(this.diagnosticResult, client.id, "");

      this.fetchCallKpiStats(client.id)
        .then(() => clientsService.diagnosticClient(this.buildDiagnosticPayload(client)))
        .then((result) => {
          this.$set(this.diagnosticResult, client.id, result || "");
        })
        .catch((err) => {
          console.error("Error requesting diagnostic:", err);
          this.$set(this.diagnosticError, client.id, true);
        })
        .finally(() => {
          this.$set(this.diagnosticLoading, client.id, false);
        });
    },
    buildDiagnosticPayload(client) {
      const callKpis = this.callKpiByClient[client.id] || {};
      return {
        companyName: client.client_name,
        contactEmail: client.contact_email || null,
        kpis: {
          cr_6m: this.toNullableNumber(client.meeting_rate),
          cr_6m_status: null,
          cr_30d: this.toNullableNumber(client.meeting_rate_this_month),
          cr_30d_status: client.alert_meeting_rate === "yes" ? "red" : null,
          cr_call_14d: this.toNullableNumber(callKpis.avg_conversion_rate_14_days),
          cr_call_14d_status: null,
          cr_call_30d: this.toNullableNumber(callKpis.avg_conversion_rate_30_days),
          cr_call_30d_status: null,
          call_14d: this.toNullableNumber(callKpis.total_cold_call_avg_14_days),
          call_14d_status: null,
          call_30d: this.toNullableNumber(callKpis.total_cold_call_avg_30_days),
          call_30d_status: null,
          sent_6m: this.toNullableNumber(client.total_contacts_day),
          sent_6m_status: null,
          sent_30d: this.toNullableNumber(client.total_contacts_day_this_month),
          sent_30d_status: this.getContactStatus(client.alert_contact_day, client.alert_contact_day_up),
          step1_6m: this.toNullableNumber(client.total_contacts_day_step1),
          step1_6m_status: null,
          step1_7d: this.toNullableNumber(client.total_contacts_day_this_month_step1),
          step1_7d_status: this.getContactStatus(client.alert_contact_day_step1, client.alert_contact_day_step1_up),
          strategic_score: this.toNullableNumber(client.cpi_score),
          strategic_score_status: client.cpi_category === "Strategic" ? "green" : null,
        },
      };
    },
    toNullableNumber(value) {
      if (value == null || value === "") return null;
      const num = parseFloat(value);
      return Number.isNaN(num) ? null : num;
    },
    getContactStatus(alertDown, alertUp) {
      if (alertDown === "yes") return "red";
      if (alertUp === "yes") return "green";
      return null;
    },
    convertDiagnosticText(text) {
      return String(text || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>");
    },
    openComments(client) {
      this.selectedClientForComments = client;
      this.commentsNotes = [];
      this.commentsModalVisible = true;
      this.commentsLoading = true;
      clientsService
        .getClientNotes(client.id, 1)
        .then((res) => {
          this.commentsNotes = this.normalizeNotesFromResponse(res);
          this.commentsLoading = false;
        })
        .catch((err) => {
          console.error("Error loading client notes:", err);
          this.commentsNotes = [];
          this.commentsLoading = false;
        });
    },
    closeComments() {
      this.commentsModalVisible = false;
    },
    normalizeNotesFromResponse(res) {
      if (!res) return [];
      if (Array.isArray(res)) return res;
      if (Array.isArray(res.result)) return res.result;
      if (Array.isArray(res.data)) return res.data;
      return [];
    },
    publishNote(message) {
      if (!this.selectedClientForComments || !(message || "").trim()) return;
      const clientId = this.selectedClientForComments.id;
      const clientNoteTopicId = 1;
      clientsService
        .createClientNote(clientId, clientNoteTopicId, (message || "").trim())
        .then(() => {
          this.clients = this.clients.map(client => {
            if(client.id == clientId) {
              client.time_warning = '0';
            }
            return client;
          });
          return clientsService.getClientNotes(clientId, clientNoteTopicId);
        })
        .then((res) => {
          //res = res.result;
          this.commentsNotes = this.normalizeNotesFromResponse(res);
        })
        .catch((err) => {
          console.error("Error saving client note:", err);
        });
    },
    formatPercent(value) {
      if (value == null || value === "") return "—";
      const num = parseFloat(value);
      if (Number.isNaN(num)) return "—";
      return `${(num).toFixed(2)}%`;
    },
    formatNumber(value) {
      if (value == null || value === "") return "—";
      const num = parseFloat(value);
      if (Number.isNaN(num)) return "—";
      return num >= 1000 ? num.toLocaleString() : String(num);
    },
    cpiCategoryFromScore(score) {
      if (score >= 0.7) return "Strategic";
      if (score >= 0.5) return "Core";
      if (score >= 0.25) return "Watch";
      return "Low Priority";
    },
    getCpiLabel(client) {
      const category = client.cpi_category || (client.cpi_score != null ? this.cpiCategoryFromScore(Number(client.cpi_score)) : null);
      const score = client.cpi_score != null ? Number(client.cpi_score).toFixed(2) : "";
      if (!category && !score) return null;
      return category ? `${category} (${score || "—"})` : `(${score})`;
    },
    getCpiBadgeStyle(client) {
      const category = client.cpi_category || (client.cpi_score != null ? this.cpiCategoryFromScore(Number(client.cpi_score)) : null);
      const colors = {
        Strategic: { bg: "#28a745", text: "#FFFFFF" },
        Core: { bg: "#007bff", text: "#FFFFFF" },
        Watch: { bg: "#fd7e14", text: "#FFFFFF" },
        "Low Priority": { bg: "#6c757d", text: "#FFFFFF" },
        "-": { bg: "#e9ecef", text: "#6c757d" },
      };
      const c = colors[category] || colors["-"];
      return { backgroundColor: c.bg, color: c.text };
    },
  },
};
</script>

<style scoped>
/* Icono de comentario en rojo cuando time_warning === 1 */
.comment-icon-warning img {
  filter: brightness(0) saturate(100%) invert(27%) sepia(98%) saturate(5000%) hue-rotate(350deg);
}

.diagnostic-result {
  white-space: normal;
}
</style>
